import type { LengthUnit } from '@tokamap/core';

export type MapKind = 'located' | 'plan';

export type { LengthUnit };

export const LENGTH_UNITS: { value: LengthUnit; label: string; }[] = [
	{ value: 'mm', label: 'Millimètres' },
	{ value: 'cm', label: 'Centimètres' },
	{ value: 'm', label: 'Mètres' },
];

export const DEFAULT_PLAN_UNIT: LengthUnit = 'cm';

const METERS_PER_UNIT: Record<LengthUnit, number> = { mm: 0.001, cm: 0.01, m: 1 };

type RawConfiguration = { kind?: unknown; unit?: unknown; tileLayerName?: unknown; grid?: unknown; rulers?: unknown; } | null | undefined;

export function declaredMapKind(configuration: RawConfiguration): MapKind | null {
	return configuration?.kind === 'located' || configuration?.kind === 'plan' ? configuration.kind : null;
}

export function mapKindOf(configuration: RawConfiguration): MapKind {
	return declaredMapKind(configuration) ?? (configuration?.tileLayerName === 'unset' ? 'plan' : 'located');
}

export function lengthUnitOf(configuration: RawConfiguration): LengthUnit {
	const unit = configuration?.unit;

	return unit === 'mm' || unit === 'cm' || unit === 'm' ? unit : 'm';
}

export function metersToUnit(meters: number, unit: LengthUnit): number {
	return roundForUnit(meters / METERS_PER_UNIT[unit], unit);
}

export function unitToMeters(value: number, unit: LengthUnit): number {
	return value * METERS_PER_UNIT[unit];
}

function roundForUnit(value: number, unit: LengthUnit): number {
	const decimals = unit === 'm' ? 3 : unit === 'cm' ? 1 : 0;
	const factor = 10 ** decimals;

	return Math.round(value * factor) / factor;
}

export function formatLength(meters: number, unit: LengthUnit): string {
	return `${metersToUnit(meters, unit).toLocaleString('fr-FR')} ${unit}`;
}

type LengthRange = { sliderMax: number; inputMax: number; step: number; };

export const HEIGHT_RANGES: Record<LengthUnit, LengthRange> = {
	m: { sliderMax: 300, inputMax: 1000, step: 1 },
	cm: { sliderMax: 1000, inputMax: 100000, step: 1 },
	mm: { sliderMax: 10000, inputMax: 1000000, step: 10 },
};

export const STROKE_WIDTH_RANGES: Record<LengthUnit, { min: number; max: number; step: number; }> = {
	m: { min: 0.1, max: 10, step: 0.1 },
	cm: { min: 1, max: 1000, step: 1 },
	mm: { min: 1, max: 10000, step: 1 },
};

export const GRID_STEPS: Record<LengthUnit, number[]> = {
	mm: [0.001, 0.005, 0.01, 0.05],
	cm: [0.01, 0.05, 0.1, 0.5],
	m: [0.1, 0.5, 1, 5],
};

export function defaultGridStep(unit: LengthUnit): number {
	return GRID_STEPS[unit][2]!;
}

export function rulersOf(configuration: RawConfiguration): boolean {
	return configuration?.rulers !== false;
}

export function gridStepOf(configuration: RawConfiguration): number | null {
	const step = (configuration?.grid as { step?: unknown; } | null | undefined)?.step;

	return typeof step === 'number' && step > 0 ? step : null;
}
